// ============================================================================
// VARIANT SELECTION ENHANCEMENT
// ============================================================================
// This file manages variant selection logic for products with split sizing
// (waist x inseam) combined with color options as well as non split variant products.
// ============================================================================

// ============================================================================
// SHARED HELPER FUNCTIONS
// ============================================================================
// These utilities are used by both split and regular variant logic

/**
 * Fully unselect an input element and clear all associated state
 * @param {HTMLInputElement} input - The input element to unselect
 */
function unselectInput(input) {
  if (!input) return;
  
  input.checked = false;
  input.removeAttribute('checked');
  input.removeAttribute('data-was-selected');
  input.classList.remove('selected', 'active', 'checked', 'is-selected');
  
  // Also clear label classes
  const label = input.closest('label') || document.querySelector(`label[for="${input.id}"]`);
  if (label) {
    label.classList.remove('selected', 'active', 'checked', 'is-selected');
  }
  
  // Clear parent classes
  const parent = input.parentElement;
  if (parent) {
    parent.classList.remove('selected', 'active', 'checked', 'is-selected');
  }
}

/**
 * Reset a fieldset's legend to "Please select"
 * @param {HTMLFieldSetElement} fieldset - The fieldset containing the legend
 */
function resetLegend(fieldset) {
  if (!fieldset) return;
  
  const legend = fieldset.querySelector('[data-selected-value]');
  if (legend) {
    legend.textContent = 'Please select';
  }
}

/**
 * Clear all selections in a fieldset
 * @param {HTMLFieldSetElement} fieldset - The fieldset to clear
 */
function clearFieldsetSelection(fieldset) {
  if (!fieldset) return;
  
  const checkedInputs = fieldset.querySelectorAll('input:checked');
  checkedInputs.forEach(input => unselectInput(input));
  resetLegend(fieldset);
}

/**
 * Extract option name from various input patterns
 * @param {HTMLInputElement} input - The input element
 * @param {HTMLFieldSetElement} fieldset - The parent fieldset
 * @returns {string|null} - The extracted option name
 */
function extractOptionName(input, fieldset) {
  let optionName = null;
  
  // Clean input name (remove newlines/whitespace)
  const cleanInputName = input.name.trim();
  
  // Method 1: Extract from name attribute "options[Color]" -> "Color"
  const nameMatch = cleanInputName.match(/options\[(.*?)\]/);
  if (nameMatch) {
    return nameMatch[1];
  }
  
  // Method 2: Check data attributes (data-color-option, data-top-size-option, etc.)
  const datasetKeys = Object.keys(input.dataset);
  const optionKey = datasetKeys.find(key => key.endsWith('Option'));
  if (optionKey) {
    // Convert "topSizeOption" to "Top Size"
    let name = optionKey.replace('Option', '');
    // Add spaces before capital letters: "TopSize" -> "Top Size"
    name = name.replace(/([A-Z])/g, ' $1').trim();
    return name;
  }
  
  // Method 3: Get from legend text
  if (fieldset) {
    const legend = fieldset.querySelector('legend');
    if (legend) {
      // "Color:" -> "Color" or "Top Size:" -> "Top Size"
      return legend.textContent.trim().replace(':', '').split('\n')[0].trim();
    }
  }
  
  return null;
}

// ============================================================================
// ATC BUTTON STATE MANAGEMENT
// ============================================================================
// Monitors all product option selections and updates the Add to Cart button
// state and text based on whether all required options are selected.

/**
 * Initialize ATC button state monitoring
 * Watches all option fieldsets and updates button when selections change
 */
function initATCButtonStateManagement() {
  const submitButton = document.querySelector('.product-form__submit');

  if (!submitButton) {
    return;
  }

  // On the gift card PDP the natural ATC is the de-emphasized secondary
  // option (the injected "Buy as a gift" CTA is primary), so its enabled
  // text is unique. Sourced from window.variantStrings like the default
  // "Add to Cart" string; keyed off the same wrapper modifier the CSS swap uses.
  const isGiftCard = submitButton.closest('.product-form__button-wrapper--gift-card') !== null;
  const enabledText = isGiftCard
    ? (window.variantStrings?.addToCartSelf || 'Buy for myself')
    : (window.variantStrings?.addToCart || 'Add to Cart');
  
  // Get all option fieldsets (both split and regular)
  const optionFieldsets = document.querySelectorAll('.product-form__input');
  
  if (optionFieldsets.length === 0) {
    return;
  }
  
  /**
   * Check if all options have selections
   * @returns {boolean} True if all options are selected
   */
  function areAllOptionsSelected() {
    let allSelected = true;
    
    optionFieldsets.forEach(fieldset => {
      // Skip quantity inputs or hidden fieldsets
      if (fieldset.querySelector('input[name="quantity"]') || 
          fieldset.style.display === 'none') {
        return;
      }
      
      const hasSelection = fieldset.querySelector('input:checked') !== null;
      
      if (!hasSelection) {
        allSelected = false;
      }
    });
    
    return allSelected;
  }
  
  /**
   * Update ATC button state and text
   */
  function updateATCButtonState() {
    const allSelected = areAllOptionsSelected();
    
    const buttonTextSpan = submitButton.querySelector('span:not(.loading__spinner):not(.sold-out-message)');
    
    if (allSelected) {
      // All options selected - enable ATC
      submitButton.removeAttribute('disabled');
      if (buttonTextSpan) {
        buttonTextSpan.textContent = enabledText;
      }
    } else {
      // Not all options selected - show "Select options"
      submitButton.setAttribute('disabled', 'disabled');
      submitButton.classList.remove('no-flicker');
      if (buttonTextSpan) {
        buttonTextSpan.textContent = 'Select an option';
      }
    }
  }
  
  // This runs on every variant-selects mutation (see the MutationObserver in
  // the DOMContentLoaded handler), so listeners must be attached idempotently
  // or they accumulate on persistent elements and leak. Expose the current
  // closure's updater so the once-bound handlers below always act on fresh DOM.
  window.atcStateUpdater = updateATCButtonState;

  // Listen for changes on all option fieldsets. Fieldsets inside <variant-selects>
  // get replaced on re-render (old ones GC with their listeners), but mark each
  // so a persistent fieldset isn't bound twice.
  optionFieldsets.forEach(fieldset => {
    if (fieldset.dataset.atcListenerBound === 'true') {
      return;
    }
    fieldset.dataset.atcListenerBound = 'true';
    fieldset.addEventListener('change', () => {
      window.atcStateUpdater?.();
    });
  });

  // The optionsCleared listener lives on the permanent `document`, so attach it
  // exactly once — otherwise every re-init stacks another handler.
  if (!window.atcOptionsClearedListenerAdded) {
    window.atcOptionsClearedListenerAdded = true;
    document.addEventListener('variant:optionsCleared', () => {
      window.atcStateUpdater?.();
    });
  }

  // Initial state update
  updateATCButtonState();
}

window.initATCButtonStateManagement = initATCButtonStateManagement;

// ============================================================================
// SPLIT VARIANT LOGIC
// ============================================================================

function initSplitVariantLogic() {
  // Only run this logic if we actually have split picker elements
  const hasSplitPickerElements = document.querySelector('.waist-option, .inseam-option');
  if (!hasSplitPickerElements) {
    return;
  }
  
  const matrixScript = document.getElementById('variant-matrix__pdp');
  if (!matrixScript) {
    return;
  }
  
  const variantMatrix = JSON.parse(matrixScript.textContent);

  // Always read the current checked values from the DOM
  function getSelectedWaist() {
    const checkedWaist = document.querySelector('.waist-option:checked');
    return checkedWaist ? checkedWaist.dataset.waist : null;
  }
  function getSelectedInseam() {
    const checkedInseam = document.querySelector('.inseam-option:checked');
    return checkedInseam ? checkedInseam.dataset.inseam : null;
  }
  
  function getSelectedColor() {
    // Look for color selection in radio buttons or select dropdowns
    const colorRadio = document.querySelector('input[type="radio"][data-color-option]:checked');
    const colorSelect = document.querySelector('select option:checked');
    
    if (colorRadio) {
      const color = colorRadio.value || colorRadio.textContent?.trim();
      return color;
    } else if (colorSelect) {
      const color = colorSelect.value || colorSelect.textContent?.trim();
      return color;
    }
    return null;
  }

  function isOptionCompatible(optionType, optionValue, currentSelections) {
    // Safety check: ensure currentSelections is an object
    if (!currentSelections || typeof currentSelections !== 'object') {
      currentSelections = { waist: null, inseam: null, color: null };
    }
    
    // Determine if ANY selection has been made (not just waist/inseam)
    const hasAnySelection = currentSelections.waist || currentSelections.inseam || currentSelections.color;
    
    // If nothing is selected yet, show all options that exist in any available variant
    // This allows users to start with ANY option (waist, inseam, OR color)
    if (!hasAnySelection) {
      const result = Object.values(variantMatrix).some(combo => {
        if (optionType === 'waist') {
            return combo.waist === optionValue && combo.available;
        } else if (optionType === 'inseam') {
            return combo.inseam === optionValue && combo.available;
        } else if (optionType === 'color') {
          return combo.variants && combo.variants[optionValue] && combo.variants[optionValue].available;
        }
        return false;
      });
      return result;
    }
    
    // If selections exist, check if this option creates a valid path to a variant
    // The key change: we build a test selection and check if ANY variant matches it
    const result = Object.values(variantMatrix).some(combo => {
      // Build test selection by combining current selections with the option being checked
      const testSelection = {
        waist: optionType === 'waist' ? optionValue : currentSelections.waist,
        inseam: optionType === 'inseam' ? optionValue : currentSelections.inseam,
        color: optionType === 'color' ? optionValue : currentSelections.color
      };
      
      // Check if this combo matches the test selection
      let matches = true;
      
      // If waist is in test selection, combo must match it
      if (testSelection.waist && combo.waist !== testSelection.waist) {
        matches = false;
      }
      
      // If inseam is in test selection, combo must match it
      if (testSelection.inseam && combo.inseam !== testSelection.inseam) {
        matches = false;
      }
      
      // If color is in test selection, combo must have that color available
      if (testSelection.color) {
        if (!combo.variants || !combo.variants[testSelection.color] || !combo.variants[testSelection.color].available) {
          matches = false;
        }
      }
      
      // Combo must be available
      if (!combo.available) {
        matches = false;
      }
      
      return matches;
    });
    return result;
  }

  function updateOptions() {
    // Get current selections from DOM
    const selectedWaist = getSelectedWaist();
    const selectedInseam = getSelectedInseam();
    const selectedColor = getSelectedColor();
    
    const currentSelections = {
      waist: selectedWaist,
      inseam: selectedInseam,
      color: selectedColor
    };
    
    // Update waist options availability based on current selections
    document.querySelectorAll('.waist-option').forEach(btn => {
      const waist = btn.dataset.waist;
      const isAvailable = isOptionCompatible('waist', waist, currentSelections);
      btn.classList.toggle('unavailable-combo', !isAvailable);
    });

    // Update inseam options availability based on current selections
    document.querySelectorAll('.inseam-option').forEach(btn => {
      const inseam = btn.dataset.inseam;
      const isAvailable = isOptionCompatible('inseam', inseam, currentSelections);
      btn.classList.toggle('unavailable-combo', !isAvailable);
    });
    
    // Update color options availability based on waist/inseam selections
    // Only disable colors if waist OR inseam is selected
    // This allows users to start with color, but disables incompatible colors after waist/inseam selection
    if (selectedWaist || selectedInseam) {
      
      document.querySelectorAll('input[data-color-option]').forEach(colorInput => {
        const colorValue = colorInput.value;
        const isAvailable = isOptionCompatible('color', colorValue, currentSelections);
        
        colorInput.classList.toggle('unavailable-combo', !isAvailable);
        
        // Also update the label
        const label = colorInput.closest('label') || document.querySelector(`label[for="${colorInput.id}"]`);
        if (label) {
          label.classList.toggle('unavailable-combo', !isAvailable);
        }
      });
        } else {
      // If no waist/inseam selected, keep all colors available      
      document.querySelectorAll('input[data-color-option]').forEach(colorInput => {
        colorInput.classList.remove('unavailable-combo');
        
        const label = colorInput.closest('label') || document.querySelector(`label[for="${colorInput.id}"]`);
        if (label) {
          label.classList.remove('unavailable-combo');
        }
      });
    }
  }

  // ============================================================================
  // Reset Selection on Color Change
  // ============================================================================
  // When a user selects a new color, check if the current waist/inseam combination
  // is available in that color. If not, clear the waist/inseam selections.
  // This prevents users from having an invalid selection state.
  
  function handleColorChange(newColor, previousColor) {
    // Only process if color actually changed
    if (!newColor || newColor === previousColor) return;
    
    const currentWaist = getSelectedWaist();
    const currentInseam = getSelectedInseam();
      
    // Check if current waist/inseam combo exists for new color
    if (currentWaist && currentInseam) {
      const comboKey = `${currentWaist}x${currentInseam}`;
      const combo = variantMatrix[comboKey];
      
      // If this combo doesn't have the new color available, clear selections
      if (!combo?.variants?.[newColor]?.available) {
        
        // Clear waist selections
        // Clear waist and inseam selections using helper functions
        const waistFieldset = document.querySelector('[data-split-size="waist"]');
        const inseamFieldset = document.querySelector('[data-split-size="inseam"]');
        clearFieldsetSelection(waistFieldset);
        clearFieldsetSelection(inseamFieldset);
        
        // Dispatch custom event for ATC button monitoring
        document.dispatchEvent(new CustomEvent('variant:optionsCleared'));
      }
    }
  }

  // ============================================================================
  // Auto-Update Gallery on Color Selection (Split Products)
  // ============================================================================
  // For SPLIT variant products (waist x inseam + color), update gallery when color changes.
  // NOTE: Regular products use initUniversalGalleryUpdate() instead.
  
  function updateGalleryForColor(colorValue) {
    if (!colorValue) return;
    
    // ============================================================================
    // ALT-ATTRIBUTE BASED APPROACH (Simple & Reliable)
    // ============================================================================
    // Find a thumbnail image whose alt attribute matches the selected color value.
    // Click that thumbnail's button to trigger the gallery's native update handler.
    
    // Find all thumbnail images in the gallery
    const thumbnailImages = document.querySelectorAll('.thumbnail img, .thumbnail-list img');
    
    // Search for matching thumbnail
    let matchingThumbnail = null;
    
    for (const img of thumbnailImages) {
      const altText = (img.alt || '').trim();
      
      // Check for exact match (case-insensitive)
      if (altText.toLowerCase() === colorValue.toLowerCase()) {
        matchingThumbnail = img;
        break;
      }
    }
    
    // If no exact match, try partial match
    if (!matchingThumbnail) {
      for (const img of thumbnailImages) {
        const altText = (img.alt || '').trim();
        if (altText.toLowerCase().includes(colorValue.toLowerCase())) {
          matchingThumbnail = img;
          break;
        }
      }
    }
    
    if (matchingThumbnail) {
      // Find the button that wraps this thumbnail
      const thumbnailButton = matchingThumbnail.closest('button');
      
      if (thumbnailButton) {
        // Click the thumbnail - gallery's native handler does the rest!
        thumbnailButton.click();     
      } 
    }
  }
  
  // Initial update - applies availability states to remaining options
  updateOptions();

  // ============================================================================
  // Track Previous Color Selection
  // ============================================================================
  // Store the currently selected color to detect changes
  let previousColorSelection = getSelectedColor();

  // Only add event listeners if they haven't been added yet
  if (!window.splitVariantListenersAdded) {
    // ============================================================================
    // Smart Selection When Disabled Options Are Clicked
    // ============================================================================
    // When clicking a disabled waist or inseam option:
    // 1. Check if selecting it would be compatible with current selections
    // 2. If compatible: Select it and retain other selections
    // 3. If not compatible: Select it and clear incompatible selections
    // Example: If waist 32 is selected and user clicks disabled inseam 30,
    // check if 32x30 is available. If yes, select both. If no, clear waist and select inseam 30.

    const previousSelections = new Map();
    
    document.querySelectorAll('.waist-option, .inseam-option').forEach(radio => {
      const optionType = radio.classList.contains('waist-option') ? 'waist' : 'inseam';
      
      // CAPTURE the current selection BEFORE any click processing
      radio.addEventListener('mousedown', (event) => {
        // Find what's currently selected for this option type
        const selector = optionType === 'waist' ? '.waist-option:checked' : '.inseam-option:checked';
        const currentlySelected = document.querySelector(selector);
        
        if (currentlySelected) {
          const currentValue = currentlySelected.dataset.waist || currentlySelected.dataset.inseam;
          previousSelections.set(optionType, {
            element: currentlySelected,
            value: currentValue
          });
        } else {
          previousSelections.set(optionType, null);
        }
      }, true); // Capture phase
      
      // Handle clicks on disabled options with smart selection logic
      radio.addEventListener('click', (event) => {
        // Check if this option is currently marked as unavailable
        if (radio.classList.contains('unavailable-combo')) {
          event.preventDefault();
          event.stopPropagation();
          
          const clickedValue = radio.dataset.waist || radio.dataset.inseam;
          
          // Get current selections for all option types
          const currentWaist = document.querySelector('.waist-option:checked')?.dataset.waist;
          const currentInseam = document.querySelector('.inseam-option:checked')?.dataset.inseam;
          const currentColor = getSelectedColor();
          
          // Build test selection with the clicked disabled option
          const testSelections = {
            waist: optionType === 'waist' ? clickedValue : currentWaist,
            inseam: optionType === 'inseam' ? clickedValue : currentInseam,
            color: currentColor
          };
                    
          // Check if this combination would be available by testing the clicked option
          const isCompatible = isOptionCompatible(optionType, clickedValue, testSelections);
                
      if (isCompatible) {
            // COMPATIBLE: Select the clicked option and keep other selections
            
            radio.checked = true;
            radio.setAttribute('checked', 'checked');
            
            // Update the legend
            const fieldset = radio.closest(`[data-split-size="${optionType}"]`);
            const legend = fieldset?.querySelector('[data-selected-value]');
            if (legend) {
              legend.textContent = clickedValue;
            }
            
          } else {
            // NOT COMPATIBLE: Select clicked option and clear incompatible selections
            
            // Now check what needs to be cleared BEFORE selecting the clicked option
            // IMPORTANT: Check each option INDEPENDENTLY with only the clicked option
            // Don't test all three together - only test pairs (clicked + other)
            
            if (optionType === 'waist') {
              
              // Clicked waist - check if current inseam is compatible (ignore color for this check)
              if (currentInseam) {
                const testWithInseam = { waist: clickedValue, inseam: currentInseam, color: null };
                
                const waistInseamCompatible = isOptionCompatible('inseam', currentInseam, testWithInseam);
                
                if (!waistInseamCompatible) {
                  // Clear inseam - not compatible with clicked waist
                  const inseamFieldset = document.querySelector('[data-split-size="inseam"]');
                  clearFieldsetSelection(inseamFieldset);
                }
              }
              
              // Check if current color is compatible with clicked waist (ignore inseam for this check)
              if (currentColor) {
                // Test ONLY waist + color, no inseam requirement
                const testWithColor = { waist: clickedValue, inseam: null, color: currentColor };
                
                const waistColorCompatible = isOptionCompatible('color', currentColor, testWithColor);
                
                if (!waistColorCompatible) {
                  // Clear color - not compatible with clicked waist
                  const colorFieldset = document.querySelector('input[data-color-option]:checked')?.closest('.product-form__input');
                  if (colorFieldset) {
                    clearFieldsetSelection(colorFieldset);
                  }
                }
              }
            } else {
              
              // Clicked inseam - check if current waist is compatible (ignore color for this check)
              if (currentWaist) {
                const testWithWaist = { waist: currentWaist, inseam: clickedValue, color: null };
                
                const waistInseamCompatible = isOptionCompatible('waist', currentWaist, testWithWaist);
                
                if (!waistInseamCompatible) {
                  // Clear waist - not compatible with clicked inseam
                  const waistFieldset = document.querySelector('[data-split-size="waist"]');
                  clearFieldsetSelection(waistFieldset);
                }
              }
              
              // Check if current color is compatible with clicked inseam (ignore waist for this check)
              if (currentColor) {
                // Test ONLY inseam + color, no waist requirement
                const testWithColor = { waist: null, inseam: clickedValue, color: currentColor };
                
                const inseamColorCompatible = isOptionCompatible('color', currentColor, testWithColor);
                
                if (!inseamColorCompatible) {
                  // Clear color - not compatible with clicked inseam
                  const colorFieldset = document.querySelector('input[data-color-option]:checked')?.closest('.product-form__input');
                  if (colorFieldset) {
                    clearFieldsetSelection(colorFieldset);
                  }
                }
              }
            }
            
            // AFTER clearing incompatible selections, NOW select the clicked option
            // This ensures it stays selected after the clear operations
            
            radio.checked = true;
            radio.setAttribute('checked', 'checked');
            
            // Remove unavailable class since we're actively selecting it
            radio.classList.remove('unavailable-combo');
            const label = radio.closest('label') || document.querySelector(`label[for="${radio.id}"]`);
            if (label) {
              label.classList.remove('unavailable-combo');
            }
            
            // Update the legend for this option
            const fieldset = radio.closest(`[data-split-size="${optionType}"]`);
            const legend = fieldset?.querySelector('[data-selected-value]');
            if (legend) {
              legend.textContent = clickedValue;
            }
          }
          
          // Dispatch custom event for ATC button monitoring
          document.dispatchEvent(new CustomEvent('variant:optionsCleared'));
          
          // Update option availability states (but the clicked option should remain selected)
          updateOptions();
          
          // ENSURE the clicked option stays selected after updateOptions()
          // This is critical because updateOptions() might try to uncheck it
          setTimeout(() => {
            if (!radio.checked) {
              radio.checked = true;
              radio.setAttribute('checked', 'checked');
              
              // Update legend again if needed
              const fieldset = radio.closest(`[data-split-size="${optionType}"]`);
              const legend = fieldset?.querySelector('[data-selected-value]');
              if (legend && legend.textContent !== clickedValue) {
                legend.textContent = clickedValue;
              }
            }
          }, 0);
          
          return false;
        }
      }, true); // Use capture phase to catch event before radio button processes it
      
      // Add change handlers for when option is actually changed
      radio.addEventListener('change', (event) => {
        // Double-check that this option is actually available
        if (radio.classList.contains('unavailable-combo')) {
          // Revert the selection
          radio.checked = false;
          return;
        }
        
        // Ensure the selected radio has proper state
        if (radio.checked) {
          const optionType = radio.classList.contains('waist-option') ? 'waist' : 'inseam';
          const optionValue = radio.dataset.waist || radio.dataset.inseam;
                    
          // Ensure the label gets updated
          const fieldset = radio.closest(`[data-split-size="${optionType}"]`);
          const legend = fieldset?.querySelector('[data-selected-value]');
          if (legend) {
            legend.textContent = optionValue;
          }
        }
        
        updateOptions();
      });
    });
    
    // ============================================================================
    // Enhanced Color Change Handler + Smart Disabled Click
    // ============================================================================
    // Add change handlers for color options with functionality:
    // - Reset incompatible waist/inseam selections when color changes
    // - Auto-update product gallery to show selected color
    // - Handle disabled color clicks with smart selection logic
    
    document.querySelectorAll('input[type="radio"][data-color-option]').forEach(colorInput => {
      // Handle clicks on disabled color options with smart selection logic
      colorInput.addEventListener('click', (event) => {
        if (colorInput.classList.contains('unavailable-combo')) {
          event.preventDefault();
          event.stopPropagation();
          
          const clickedColor = colorInput.value;
          
          const currentWaist = getSelectedWaist();
          const currentInseam = getSelectedInseam();
          
          // Build test selection with clicked color
          const testSelections = {
            waist: currentWaist,
            inseam: currentInseam,
            color: clickedColor
          };
                    
          // Check if this combination would be available
          const isCompatible = isOptionCompatible('color', clickedColor, testSelections);
                    
          if (isCompatible) {
            // COMPATIBLE: Select the color and keep waist/inseam
            
            colorInput.checked = true;
            colorInput.setAttribute('checked', 'checked');
            
            // Remove unavailable class since we're actively selecting it
            colorInput.classList.remove('unavailable-combo');
            const label = colorInput.closest('label') || document.querySelector(`label[for="${colorInput.id}"]`);
            if (label) {
              label.classList.remove('unavailable-combo');
            }
            
            // Update legend
            const fieldset = colorInput.closest('.product-form__input');
            const legend = fieldset?.querySelector('[data-selected-value]');
            if (legend) {
              legend.textContent = clickedColor;
            }
            
          } else {
            // NOT COMPATIBLE: Select color and clear incompatible waist/inseam
            
            // Check if waist is compatible with clicked color (ignore inseam)
            if (currentWaist) {
              const testWithWaist = { waist: currentWaist, inseam: null, color: clickedColor };
              
              const waistCompatible = isOptionCompatible('waist', currentWaist, testWithWaist);
              
              if (!waistCompatible) {
                const waistFieldset = document.querySelector('[data-split-size="waist"]');
                clearFieldsetSelection(waistFieldset);
              }
            }
            
            // Check if inseam is compatible with clicked color (ignore waist)
            if (currentInseam) {
              const testWithInseam = { waist: null, inseam: currentInseam, color: clickedColor };              
              const inseamCompatible = isOptionCompatible('inseam', currentInseam, testWithInseam);
              
              if (!inseamCompatible) {
                const inseamFieldset = document.querySelector('[data-split-size="inseam"]');
                clearFieldsetSelection(inseamFieldset);
              }
            }
            
            // AFTER clearing incompatible selections, NOW select the clicked color
            // This ensures it stays selected after the clear operations
           
            colorInput.checked = true;
            colorInput.setAttribute('checked', 'checked');
            
            // Remove unavailable class since we're actively selecting it
            colorInput.classList.remove('unavailable-combo');
            const label = colorInput.closest('label') || document.querySelector(`label[for="${colorInput.id}"]`);
            if (label) {
              label.classList.remove('unavailable-combo');
            }
            
            // Update legend
            const fieldset = colorInput.closest('.product-form__input');
            const legend = fieldset?.querySelector('[data-selected-value]');
            if (legend) {
              legend.textContent = clickedColor;
            }
          }
          
          // Dispatch custom event for ATC button monitoring
          document.dispatchEvent(new CustomEvent('variant:optionsCleared'));
          
          // Update options and gallery
          updateOptions();
          updateGalleryForColor(clickedColor);
          
          // Update previous color tracking
          previousColorSelection = clickedColor;
          
          // ENSURE the clicked color stays selected after updateOptions()
          // This is critical because updateOptions() might try to uncheck it
          setTimeout(() => {
            if (!colorInput.checked) {
              colorInput.checked = true;
              colorInput.setAttribute('checked', 'checked');
              
              // Update legend again if needed
              const fieldset = colorInput.closest('.product-form__input');
              const legend = fieldset?.querySelector('[data-selected-value]');
              if (legend && legend.textContent !== clickedColor) {
                legend.textContent = clickedColor;
              }
            }
          }, 0);
          
          return false;
        }
      }, true);
      
      // Regular change handler for enabled color selections
      colorInput.addEventListener('change', (event) => {
        const newColor = getSelectedColor();
        
        //Handle color change and reset incompatible selections
        handleColorChange(newColor, previousColorSelection);
        
        //Update gallery to show selected color
        updateGalleryForColor(newColor);
        
        // Update previous color tracking
        previousColorSelection = newColor;
        
        // Update option availability states
        updateOptions();
      });
    });
    
    window.splitVariantListenersAdded = true;
  }
}

window.initSplitVariantLogic = initSplitVariantLogic;

// ============================================================================
// REGULAR VARIANT LOGIC - Non-Split Products (Color + Size, etc.)
// ============================================================================
// Handles dynamic option enabling/disabling for regular variant products
// (e.g., shirts with Color + Size) based on availability

function initRegularVariantLogic() {
  // Only run for NON-split products
  const isSplitProduct = document.querySelector('.waist-option, .inseam-option');
  if (isSplitProduct) {
    return;
  }
  
  // Get product data with detailed debugging
  let product = window.ShopifyProduct || getProductFromDOM();
  
  if (!product || !product.variants) {
    return;
  }
  
  // Get all option fieldsets - use class selector for regular products
  const optionFieldsets = document.querySelectorAll('.product-form__input:not([data-split-size])');
  
  
  if (optionFieldsets.length === 0) {
    return;
  }
  
  // Build a map of fieldset to option position
  const fieldsetMap = [];
  optionFieldsets.forEach((fieldset, index) => {
    // Find the first input to get option name
    const firstInput = fieldset.querySelector('input[type="radio"]');
    if (firstInput) {      
      // Use helper function to extract option name
      const optionName = extractOptionName(firstInput, fieldset);
            
      // Find matching option in product.options to get position (case-insensitive)
      const optionIndex = product.options?.findIndex(opt => 
        opt.toLowerCase() === optionName?.toLowerCase()
      );
      const position = optionIndex >= 0 ? optionIndex + 1 : index + 1; // 1-indexed
      
      fieldsetMap.push({ fieldset, position, optionName });
    }
  });
  
  // Function to get currently selected values for all options
  function getCurrentSelections() {
    const selections = {};
    fieldsetMap.forEach(({ fieldset, position }) => {
      const selectedInput = fieldset.querySelector('input:checked');
      if (selectedInput) {
        selections[position] = selectedInput.value;
      }
    });
    return selections;
  }
  
  // Function to check if an option value is available given current selections
  function isOptionValueAvailable(optionPosition, optionValue, currentSelections) {
    // Build a test selection with this option value
    const testSelection = { ...currentSelections };
    testSelection[optionPosition] = optionValue;
    
    // Check if ANY variant matches this combination
    return product.variants.some(variant => {
      // Check if variant matches all selected options
      const matches = Object.entries(testSelection).every(([pos, val]) => {
        const position = parseInt(pos);
        const variantOption = variant[`option${position}`];
        return variantOption === val;
      });
      
      // Variant must match AND be available
      return matches && variant.available;
    });
  }
  
  // Function to update all option availability
  function updateRegularOptions() {
    const currentSelections = getCurrentSelections();
        
    // Update each option fieldset
    fieldsetMap.forEach(({ fieldset, position, optionName }) => {
      const inputs = fieldset.querySelectorAll('input[type="radio"]');
      
      // Check if this is a color option (similar to split variant logic)
      const isColorOption = optionName?.toLowerCase() === 'color';
      
      inputs.forEach(input => {
        const value = input.value;
        
        // COLORS ALWAYS CLICKABLE (don't disable based on other selections)
        if (isColorOption) {
          input.classList.remove('unavailable-combo');
          const label = input.closest('label') || document.querySelector(`label[for="${input.id}"]`);
          if (label) {
            label.classList.remove('unavailable-combo');
          }
          return; // Skip availability check for colors
        }
        
        // For non-color options, check availability
        const isAvailable = isOptionValueAvailable(position, value, currentSelections);
        // Toggle unavailable class
        input.classList.toggle('unavailable-combo', !isAvailable);
        
        // Also update the label
        const label = input.closest('label') || document.querySelector(`label[for="${input.id}"]`);
        if (label) {
          label.classList.toggle('unavailable-combo', !isAvailable);
        }
      });
    });
  }
  
  // Add change listeners to all option inputs
  fieldsetMap.forEach(({ fieldset, optionName }) => {
    const inputs = fieldset.querySelectorAll('input[type="radio"]');
    
    const isColorOption = optionName?.toLowerCase() === 'color';
    
    inputs.forEach(input => {
      input.addEventListener('change', () => {
        
        // ============================================================================
        // COLOR CHANGE: Clear incompatible size selections
        // ============================================================================
        // When color changes, check if current size selection is compatible
        // If not, clear the size selection (similar to split variant logic)
        
        if (isColorOption) {
          const newColor = input.value;          
          // Find all non-color fieldsets (size, style, etc.)
          fieldsetMap.forEach(({ fieldset: otherFieldset, optionName: otherOptionName, position: otherPosition }) => {
            if (otherOptionName?.toLowerCase() === 'color') return; // Skip color itself
            
            const selectedOption = otherFieldset.querySelector('input:checked');
            if (!selectedOption) return; // No selection to check
            
            const selectedValue = selectedOption.value;
            
            // Check if this selected value is compatible with the new color
            const testSelection = { [otherPosition]: selectedValue };
            
            // Get color position
            const colorFieldsetData = fieldsetMap.find(f => f.optionName?.toLowerCase() === 'color');
            if (colorFieldsetData) {
              testSelection[colorFieldsetData.position] = newColor;
            }
            
            // Check if any variant matches this combination
            const isCompatible = product.variants.some(variant => {
              const matches = Object.entries(testSelection).every(([pos, val]) => {
                const position = parseInt(pos);
                return variant[`option${position}`] === val;
              });
              return matches && variant.available;
            });
            
            if (!isCompatible) {              
              // Clear the incompatible selection using helper functions
              unselectInput(selectedOption);
              resetLegend(otherFieldset);
            }
          });
        }
        
        updateRegularOptions();
      });
    });
  });
  
  // ============================================================================
  // SMART SELECTION WHEN DISABLED OPTION CLICKED (Regular Variants)
  // ============================================================================
  // When user clicks a disabled option:
  // 1. Check if selecting it would be compatible with current selections
  // 2. If compatible: Select it and retain other selections
  // 3. If not compatible: Select it and clear incompatible selections
  // Example: If Color Red is selected and user clicks disabled Size M,
  // check if Red/M is available. If yes, select both. If no, clear Red and select M.
  
  const previousSelections = new Map();
  
  fieldsetMap.forEach(({ fieldset, optionName, position }) => {
    const inputs = fieldset.querySelectorAll('input[type="radio"]');
    
    inputs.forEach(input => {
      // Capture current selection on mousedown (before click)
      input.addEventListener('mousedown', (event) => {
        const currentlySelected = fieldset.querySelector('input:checked');
        
        if (currentlySelected) {
          previousSelections.set(optionName, {
            element: currentlySelected,
            value: currentlySelected.value
          });
            } else {
          previousSelections.set(optionName, null);
        }
      }, true); // Capture phase
      
      // Handle clicks on disabled options with smart selection logic
      input.addEventListener('click', (event) => {
        if (input.classList.contains('unavailable-combo')) {
          event.preventDefault();
          event.stopPropagation();
          
          const clickedValue = input.value;
          
          // Get current selections for all options
          const currentSelections = getCurrentSelections();
          
          // Build test selection with the clicked disabled option
          const testSelections = { ...currentSelections };
          testSelections[position] = clickedValue;
          
          // Check if this combination would be available
          const isCompatible = product.variants.some(variant => {
            const matches = Object.entries(testSelections).every(([pos, val]) => {
              const position = parseInt(pos);
              return variant[`option${position}`] === val;
            });
            return matches && variant.available;
          });
          
          if (isCompatible) {
            // COMPATIBLE: Select the clicked option and keep other selections
            input.checked = true;
            input.setAttribute('checked', 'checked');
            
            // Update the legend
            const legend = fieldset.querySelector('[data-selected-value]');
            if (legend) {
              legend.textContent = clickedValue;
            }
          } else {
            // NOT COMPATIBLE: Select clicked option and clear incompatible selections
            
            // First, select the clicked option
            input.checked = true;
            input.setAttribute('checked', 'checked');
            
            // Update legend for this option
            const legend = fieldset.querySelector('[data-selected-value]');
            if (legend) {
              legend.textContent = clickedValue;
            }
            
            // Now check all other options and clear if incompatible
            fieldsetMap.forEach(({ fieldset: otherFieldset, position: otherPosition, optionName: otherOptionName }) => {
              // Skip the current option
              if (otherOptionName === optionName) return;
              
              const selectedOtherOption = otherFieldset.querySelector('input:checked');
              if (!selectedOtherOption) return; // No selection to check
              
              // Build test with clicked option and this other option
              const compatibilityTest = { 
                [position]: clickedValue, 
                [otherPosition]: selectedOtherOption.value 
              };
              
              // Check if this combo is available
              const otherIsCompatible = product.variants.some(variant => {
                const matches = Object.entries(compatibilityTest).every(([pos, val]) => {
                  const position = parseInt(pos);
                  return variant[`option${position}`] === val;
                });
                return matches && variant.available;
              });
              
              if (!otherIsCompatible) {
                // Clear this incompatible option
                clearFieldsetSelection(otherFieldset);
              }
            });
          }
          
          // Dispatch custom event for ATC button monitoring
          document.dispatchEvent(new CustomEvent('variant:optionsCleared'));

          // Dispatch change event to update UI
          const changeEvent = new Event('change', { bubbles: true });
          fieldset.dispatchEvent(changeEvent);
          
          // Update availability states
          updateRegularOptions();
          
          return false;
        }
      }, true); // Capture phase
    });
  });
  
  // Initial update
  updateRegularOptions();
}

window.initRegularVariantLogic = initRegularVariantLogic;

// ============================================================================
// UNIVERSAL GALLERY UPDATE - ALL Products (Split & Regular)
// ============================================================================
// This function updates the product gallery when a color is selected.
// It works for BOTH split variant products (waist x inseam + color) AND
// regular products (color + size, color only, etc.)

function initUniversalGalleryUpdate() {  
  // Find all color input elements (works for both split and regular products)
  const colorInputs = document.querySelectorAll('input[type="radio"][data-color-option], input[type="radio"][name*="Color"], select[name*="Color"]');
  
  if (colorInputs.length === 0) {
    return;
  }
    
  // Add change listener to each color input
  colorInputs.forEach(input => {
    // Skip if this is a split picker (handled by initSplitVariantLogic)
    const isSplitProduct = document.querySelector('.waist-option, .inseam-option');
    if (isSplitProduct) {
      return;
    }
    
    input.addEventListener('change', function(e) {
      const selectedColor = e.target.value;
      
      // Find the variant with this color that has a featured image
      const product = window.ShopifyProduct || getProductFromDOM();
      if (!product || !product.variants) {
        return;
      }
      
      // Look for a variant with the selected color
      let variantWithImage = null;
      
      for (const variant of product.variants) {
        // Check if this variant matches the selected color
        // Color could be in option1, option2, or option3 depending on product setup
        const matchesColor = 
          variant.option1 === selectedColor ||
          variant.option2 === selectedColor ||
          variant.option3 === selectedColor;
        
        if (matchesColor && variant.featured_media?.id) {
          variantWithImage = variant;
          break;
        }
      }
      
      // Update gallery if we found a matching variant
      if (variantWithImage?.featured_media?.id) {
        updateGalleryToMedia(variantWithImage.featured_media.id);
      } 
    });
  });
}

// Helper function to update gallery to a specific media ID
// Works for both split and regular products
function updateGalleryToMedia(mediaId) {
  
  const sectionId = document.querySelector('variant-selects')?.dataset?.section || 
                    document.querySelector('[data-section-id]')?.dataset?.sectionId;
  
  if (!sectionId) {
    return;
  }
  
  const fullMediaId = `${sectionId}-${mediaId}`;
  
  // Method 1: Try to use media gallery's setActiveMedia method (PREFERRED)
  const mediaGallery = document.querySelector('media-gallery');
  
  if (mediaGallery && typeof mediaGallery.setActiveMedia === 'function') {
    try {
      // Call setActiveMedia
      mediaGallery.setActiveMedia(fullMediaId, true);
      
      // ENHANCED: Also manually scroll the slider viewer to the active item
      const viewer = mediaGallery.querySelector('[id^="GalleryViewer"]');
      const activeMediaItem = mediaGallery.querySelector(`[data-media-id="${fullMediaId}"]`);
      
      if (viewer && activeMediaItem) {
        
        // Force scroll the slider to the active item
        const slider = viewer.querySelector('[id^="Slider-"]');
        if (slider) {
          slider.scrollTo({
            left: activeMediaItem.offsetLeft,
            behavior: 'smooth'
          });
        }
        
        // Also ensure the active media has is-active class
        mediaGallery.querySelectorAll('.product__media-item').forEach(item => {
          item.classList.remove('is-active');
        });
        activeMediaItem.classList.add('is-active');
      }
      
      return; 
    } catch (error) {
      console.error('Error updating gallery to media:', error);
    }
  }
  
  // Method 2: Fallback - Click the thumbnail button
  const thumbnailSelector = `[data-target="${fullMediaId}"] button`;
  const thumbnail = document.querySelector(thumbnailSelector);
  if (thumbnail) {    
    // Try multiple click methods for robustness
    thumbnail.click(); // Native click
    
    // Also dispatch a custom click event
    const clickEvent = new MouseEvent('click', {
      bubbles: true,
      cancelable: true,
      view: window
    });
    thumbnail.dispatchEvent(clickEvent);
        
    // Verify main gallery updated (with retry logic)
    setTimeout(() => {
      const activeMedia = document.querySelector('.product__media-item.is-active');      
      // If main gallery didn't update, try direct DOM manipulation
      if (!activeMedia || activeMedia.dataset.mediaId !== fullMediaId) {        
        // Find the target media item and make it active
        const targetMediaItem = document.querySelector(`[data-media-id="${fullMediaId}"]`);
        if (targetMediaItem) {
          // Remove active class from all items
          document.querySelectorAll('.product__media-item').forEach(item => {
            item.classList.remove('is-active');
          });
          
          // Add active class to target
          targetMediaItem.classList.add('is-active');
          
          // Scroll into view
          targetMediaItem.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } 
      }
    }, 100);
    return;
  }
}

// Helper function to get product data from DOM
function getProductFromDOM() {
  // Try to get from global Shopify object
  if (window.ShopifyProduct) {
    return window.ShopifyProduct;
  }
  
  // Try to get from script tag with data-product-json (new approach - most reliable)
  const productScript = document.querySelector('script[data-product-json]');
  if (productScript) {
    try {
      return JSON.parse(productScript.textContent);
    } catch (e) {
      console.error('Error parsing product script:', e);
    }
  }
  
  // Fallback: Try other selectors
  const productData = document.querySelector('[data-product-json]');
  if (productData && productData.textContent) {
    try {
      return JSON.parse(productData.textContent);
    } catch (e) {
      console.error('Error parsing product data:', e);
    }
  }
  
  return null;
}

// Expose helper function globally for split variant logic to use
window.updateGalleryToMedia = updateGalleryToMedia;

document.addEventListener('DOMContentLoaded', function() {
  initSplitVariantLogic();
  initRegularVariantLogic(); // NEW: For non-split products
  initUniversalGalleryUpdate();
  initATCButtonStateManagement(); // NEW: Monitor ATC button state

  // Watch for re-renders of the variant picker
  const variantSelects = document.querySelector('variant-selects');
  if (variantSelects) {
    const observer = new MutationObserver(() => {
      // Short delay to allow DOM to settle
      setTimeout(() => {
        initSplitVariantLogic();
        initRegularVariantLogic(); // NEW: For non-split products
        initATCButtonStateManagement(); // NEW: Monitor ATC button state
      }, 100);
    });
    observer.observe(variantSelects, { childList: true, subtree: true });
  } 
});

document.addEventListener('shopify:section:load', function() {
  initSplitVariantLogic();
  initRegularVariantLogic(); // NEW: For non-split products
});

// Also listen for product-info loaded event (in case product data loads after DOMContentLoaded)
document.addEventListener('product-info:loaded', function() {
  initRegularVariantLogic();
});